import React, { useCallback, useEffect, useRef, useState } from "react";

const PAGE_SIZES = [10, 25, 50, 100];

// Builds the list of page buttons: 1 … 4 5 [6] 7 8 … 20
const pageNumbers = (current, totalPages) => {
  const pages = [];
  const add = (p) => {
    if (p >= 1 && p <= totalPages && !pages.includes(p)) pages.push(p);
  };
  add(1);
  for (let p = current - 2; p <= current + 2; p++) add(p);
  add(totalPages);
  pages.sort((a, b) => a - b);
  const withGaps = [];
  pages.forEach((p, i) => {
    if (i > 0 && p - pages[i - 1] > 1) withGaps.push("gap-" + p);
    withGaps.push(p);
  });
  return withGaps;
};

// Footer shared by every paginated table: page size, range and page buttons
export const PagerFooter = ({
  page,
  size,
  total,
  totalPages,
  onPageChange,
  onSizeChange,
}) => {
  const from = total === 0 ? 0 : (page - 1) * size + 1;
  const to = Math.min(page * size, total);
  return (
        <div className="card-footer d-flex flex-wrap justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <select
              className="custom-select custom-select-sm mr-2"
              style={{ width: 75 }}
              value={size}
              onChange={(e) => onSizeChange(Number(e.target.value))}
            >
              {PAGE_SIZES.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <small className="text-muted">
              {from}-{to} sur {total}
            </small>
          </div>
          {totalPages > 1 && (
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => onPageChange(page - 1)}
                  disabled={page === 1}
                >
                  &laquo;
                </button>
              </li>
              {pageNumbers(page, totalPages).map((p) =>
                typeof p === "string" ? (
                  <li key={p} className="page-item disabled">
                    <span className="page-link">…</span>
                  </li>
                ) : (
                  <li key={p} className="page-item">
                    <button
                      className="page-link"
                      style={
                        p === page
                          ? { background: "#343a40", color: "#fff" }
                          : { color: "#343a40" }
                      }
                      onClick={() => onPageChange(p)}
                    >
                      {p}
                    </button>
                  </li>
                )
              )}
              <li
                className={`page-item ${page === totalPages ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => onPageChange(page + 1)}
                  disabled={page === totalPages}
                >
                  &raquo;
                </button>
              </li>
            </ul>
          )}
        </div>
  );
};

/*
 * Server-side paginated table inside a card, with search and loader.
 *  - fetchPage({ page (0-based), size, search }) must return a promise resolving
 *    to an axios response whose data is { success: Page } or { error }.
 *  - columns: [{ label, width, align, render(row) }]
 *  - reloadKey: change it to force a reload of the current page.
 */
const PagedCardTable = ({
  title,
  icon,
  fetchPage,
  columns,
  reloadKey,
  searchPlaceholder = "Rechercher",
  emptyText = "Aucun élément",
  badgeClass = "badge-dark",
}) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const requestId = useRef(0);

  // debounce the search box, and go back to page 1 when it changes
  const lastSearch = useRef("");
  useEffect(() => {
    const timer = setTimeout(() => {
      const value = searchInput.trim();
      if (value !== lastSearch.current) {
        lastSearch.current = value;
        setSearch(value);
        setPage(1);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const load = useCallback(() => {
    const current = ++requestId.current;
    setLoading(true);
    setError(false);
    fetchPage({ page: page - 1, size, search })
      .then((response) => {
        if (current !== requestId.current) return;
        const data = response.data;
        if (!data || data.error || !data.success) {
          setError(true);
          return;
        }
        setRows(data.success.content);
        setTotal(data.success.totalElements);
        setTotalPages(data.success.totalPages);
      })
      .catch(() => {
        if (current === requestId.current) setError(true);
      })
      .finally(() => {
        if (current === requestId.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, search, reloadKey]);

  useEffect(() => {
    load();
  }, [load]);

  // after deleting the last row of the last page, step back one page
  useEffect(() => {
    if (!loading && !error && rows.length === 0 && page > 1 && totalPages > 0) {
      setPage(Math.min(page - 1, totalPages));
    }
  }, [loading, error, rows, page, totalPages]);

  return (
    <div
      className="card card-outline card-dark mt-3"
      style={{ borderRadius: 8, boxShadow: "0 1px 6px rgba(0,0,0,.08)" }}
    >
      <div className="card-header">
        <h3 className="card-title">
          {icon && <i className={`${icon} mr-2`}></i>}
          {title}
          <span className={`badge ${badgeClass} ml-2`}>{total}</span>
        </h3>
        <div className="card-tools" style={{ minWidth: 220 }}>
          <input
            type="search"
            className="form-control form-control-sm"
            placeholder={searchPlaceholder}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
      </div>
      <div className="card-body p-0" style={{ position: "relative" }}>
        {loading && (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 5,
              background: "rgba(255,255,255,.7)",
            }}
          >
            <div className="spinner-border text-dark" role="status">
              <span className="sr-only">Chargement...</span>
            </div>
          </div>
        )}
        <div className="table-responsive" style={{ minHeight: 120 }}>
          <table className="table table-striped table-hover mb-0">
            <thead className="thead-light">
              <tr>
                {columns.map((column, i) => (
                  <th
                    key={i}
                    className={column.align === "center" ? "text-center" : ""}
                    style={column.width ? { width: column.width } : undefined}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  {columns.map((column, i) => (
                    <td
                      key={i}
                      className={column.align === "center" ? "text-center" : ""}
                    >
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
              {!loading && !error && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="text-center text-muted py-4"
                  >
                    {emptyText}
                  </td>
                </tr>
              )}
              {!loading && error && (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="text-center text-danger py-4"
                  >
                    Erreur lors du chargement.{" "}
                    <button className="btn btn-link p-0" onClick={load}>
                      Réessayer
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <PagerFooter
        page={page}
        size={size}
        total={total}
        totalPages={totalPages}
        onPageChange={setPage}
        onSizeChange={(newSize) => {
          setSize(newSize);
          setPage(1);
        }}
      />
    </div>
  );
};

export default PagedCardTable;
